import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input/input';

import API from '@api/API';

import {
  fetchTokenIsValid,
  useFetchTokenIsValid,
  useIsValidTokenQuery,
} from '@queries/UserQueries';
import { useMagic } from '@queries/index';

import { useOneSignal } from '@hooks/useOneSignal';

import { useLocation, useNavigate, useQueryParams } from '@router/index';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { Input } from '@components/form/Input';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { Screen } from '@components/layout/Screen';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useAppContext } from '../contexts/AppContext';

const authWithMagicLink = (payload: { userId: string; code: string }) => {
  return API.post<{ user: any; token: string }, { userId: string; code: string }>(
    '/v1/user/auth/magic-link/validate',
    payload,
  );
};

type RedirectType = 'entertainer' | 'promoter' | 'venue' | 'direct-message' | 'url';

export const MagicLinkScreen = createScreenComponent<
  {},
  {
    userId?: string;
    code?: string;
    redirect?: RedirectType;
    entertainerId?: string;
    url?: string;
  }
>(({ f7route }) => {
  const { userId, code, redirect, entertainerId, url } = f7route.query ?? {};

  const navigate = useNavigate();
  const fetchTokenIsValid = useFetchTokenIsValid();
  const { updateToken, navigateAfterLogin } = useAppContext();

  const redirectTimeoutRef = useRef<NodeJS.Timeout>(null);
  const queryClient = useQueryClient();
  const { pathname } = useLocation();

  useEffect(() => {
    if (userId && code) {
      const authWithMagicLinkAsync = async () => {
        try {
          const { tokenIsValid } = await fetchTokenIsValid();

          if (!tokenIsValid) {
            const { token } = await authWithMagicLink({ userId, code });

            if (!token) {
              throw new Error('Failed to authenticate with token');
            }

            updateToken(token);

            await Promise.all([
              queryClient.invalidateQueries({ queryKey: ['user'] }),
              queryClient.invalidateQueries({ queryKey: ['tokenIsValid'] }),
            ]);
          }

          let queryParamsString = '';
          if (redirect === 'direct-message') {
            if (userId && entertainerId) {
              queryParamsString = `?currentUserId=${userId}&entertainerId=${entertainerId}`;
            }
          }

          if (redirect === 'url') {
            const params = new URLSearchParams({
              url,
            });
            queryParamsString = `?${params.toString()}`;
          }

          await navigateAfterLogin(
            redirect ? `/magic-link/${redirect}${queryParamsString}` : '/home',
            {
              animate: false,
            },
          );
        } catch (e) {
          await navigateAfterLogin();
        }
      };

      authWithMagicLinkAsync();
    } else {
      if (pathname === '/magic-link') {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = setTimeout(() => {
          navigate('/');
        }, 3000);
      }
    }

    return () => {
      clearTimeout(redirectTimeoutRef.current);
    };
  }, [userId, code]);

  const [appendedText, setAppendedText] = useState('');

  useEffect(() => {
    const loadingInterval = setInterval(() => {
      setAppendedText((prev) => (prev.length === 3 ? '' : `${prev}.`));
    }, 500);

    return () => {
      clearInterval(loadingInterval);
    };
  }, []);

  const text = useMemo(() => `Please wait${appendedText}`, [appendedText]);

  return (
    <Screen name="MagicLink Screen" hideToolbar>
      <Screen.Content>
        <EmptyState title="Signing you in" text={text} />
      </Screen.Content>
    </Screen>
  );
});
